import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ThemeToggle from "./ThemeToggle";

const STATUSES = [
  { value: "backlog", label: "Backlog" },
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "done", label: "Done" },
  { value: "archived", label: "Archived" },
];

const PRIORITIES = ["P0", "P1", "P2", "P3", "P4"];

const PRIORITY_COLORS = {
  P0: { bg: "rgb(220, 38, 38)", text: "white" }, // red-600
  P1: { bg: "rgb(234, 88, 12)", text: "white" }, // orange-600
  P2: { bg: "rgb(234, 179, 8)", text: "black" }, // yellow-500
  P3: { bg: "rgb(34, 197, 94)", text: "white" }, // green-500
  P4: { bg: "rgb(148, 163, 184)", text: "white" }, // slate-400
};

const SortableTask = ({ task, onEdit, onDelete, onStatusChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.P4;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="roadmap-task-card"
      {...attributes}
    >
      <div className="roadmap-task-header">
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <div
            {...listeners}
            style={{
              cursor: "grab",
              padding: "0.25rem",
              fontSize: "1.25rem",
            }}
            title="Drag to reorder"
          >
            ⠿
          </div>
          <span
            className="roadmap-priority-badge"
            style={{
              backgroundColor: priorityColor.bg,
              color: priorityColor.text,
            }}
          >
            {task.priority}
          </span>
          <h3 className="roadmap-task-title">{task.name}</h3>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <select
            value={task.status}
            onChange={(e) => onStatusChange(task.id, e.target.value)}
            className="roadmap-status-select"
            onClick={(e) => e.stopPropagation()}
          >
            {STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          <button
            className="btn-icon"
            onClick={() => onEdit(task)}
            title="Edit task"
          >
            ✏️
          </button>
          <button
            className="btn-icon"
            onClick={() => onDelete(task.id)}
            title="Delete task"
          >
            🗑️
          </button>
        </div>
      </div>
      {task.desc && <p className="roadmap-task-desc">{task.desc}</p>}
    </div>
  );
};

const TaskDialog = ({ task, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    task || {
      name: "",
      desc: "",
      status: "backlog",
      priority: "P2",
    }
  );

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Task name is required");
      return;
    }
    onSave(formData);
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-content" style={{ maxWidth: "600px" }}>
        <h2 style={{ marginBottom: "1.5rem" }}>
          {task ? "Edit Task" : "Add New Task"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Task Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Implement user authentication"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              placeholder="Detailed description of the task..."
              value={formData.desc}
              onChange={(e) =>
                setFormData({ ...formData, desc: e.target.value })
              }
              rows={4}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                {STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: "1.5rem" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {task ? "Update Task" : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Roadmap = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load tasks from localStorage or API
  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      // For now, use localStorage. Later can switch to API
      const storedTasks = localStorage.getItem("roadmap-tasks");
      if (storedTasks) {
        setTasks(JSON.parse(storedTasks));
      }
    } catch (error) {
      console.error("Error loading tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveTasks = (updatedTasks) => {
    setTasks(updatedTasks);
    localStorage.setItem("roadmap-tasks", JSON.stringify(updatedTasks));
  };

  const handleAddTask = (taskData) => {
    const newTask = {
      ...taskData,
      id: Date.now().toString(),
      rank: tasks.length,
      createdAt: new Date().toISOString(),
    };
    saveTasks([...tasks, newTask]);
    setShowDialog(false);
  };

  const handleEditTask = (taskData) => {
    const updatedTasks = tasks.map((t) =>
      t.id === editingTask.id ? { ...t, ...taskData } : t
    );
    saveTasks(updatedTasks);
    setShowDialog(false);
    setEditingTask(null);
  };

  const handleDeleteTask = (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      saveTasks(tasks.filter((t) => t.id !== taskId));
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    const updatedTasks = tasks.map((t) =>
      t.id === taskId ? { ...t, status: newStatus } : t
    );
    saveTasks(updatedTasks);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = tasks.findIndex((t) => t.id === active.id);
      const newIndex = tasks.findIndex((t) => t.id === over.id);

      const reorderedTasks = arrayMove(tasks, oldIndex, newIndex).map(
        (task, index) => ({
          ...task,
          rank: index,
        })
      );

      saveTasks(reorderedTasks);
    }
  };

  const exportToJSON = () => {
    const dataStr = JSON.stringify(tasks, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `roadmap-${new Date().toISOString().split("T")[0]}.json`;
    link.click();
  };

  const exportToMarkdown = () => {
    let markdown = "# Roadmap\n\n";

    STATUSES.forEach((status) => {
      const statusTasks = tasks.filter((t) => t.status === status.value);
      if (statusTasks.length > 0) {
        markdown += `## ${status.label}\n\n`;
        statusTasks.forEach((task) => {
          markdown += `### [${task.priority}] ${task.name}\n`;
          if (task.desc) {
            markdown += `${task.desc}\n`;
          }
          markdown += "\n";
        });
      }
    });

    const dataBlob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `roadmap-${new Date().toISOString().split("T")[0]}.md`;
    link.click();
  };

  // Filter tasks
  const filteredTasks = tasks
    .filter((task) => {
      if (filterStatus !== "all" && task.status !== filterStatus) return false;
      if (filterPriority !== "all" && task.priority !== filterPriority)
        return false;
      if (
        searchQuery &&
        !task.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !task.desc.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;
      return true;
    })
    .sort((a, b) => a.rank - b.rank);

  const taskStats = {
    total: tasks.length,
    byStatus: STATUSES.reduce((acc, status) => {
      acc[status.value] = tasks.filter((t) => t.status === status.value).length;
      return acc;
    }, {}),
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading roadmap...
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Roadmap</h1>
          <p className="dashboard-subtitle">
            Track tasks, issues, and enhancements
          </p>
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <ThemeToggle />
          <button className="btn btn-secondary" onClick={() => navigate("/")}>
            ← Back to Dashboard
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingTask(null);
              setShowDialog(true);
            }}
          >
            ➕ Add Task
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="roadmap-stats">
        <div className="stat-card">
          <div className="stat-label">Total Tasks</div>
          <div className="stat-value">{taskStats.total}</div>
        </div>
        {STATUSES.map((status) => (
          <div key={status.value} className="stat-card">
            <div className="stat-label">{status.label}</div>
            <div className="stat-value">{taskStats.byStatus[status.value]}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="roadmap-filters">
        <input
          type="text"
          className="form-input"
          placeholder="🔍 Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1 }}
        />
        <select
          className="form-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Statuses</option>
          {STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="all">All Priorities</option>
          {PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {priority}
            </option>
          ))}
        </select>
        <button className="btn btn-secondary" onClick={exportToJSON}>
          📥 Export JSON
        </button>
        <button className="btn btn-secondary" onClick={exportToMarkdown}>
          📝 Export Markdown
        </button>
      </div>

      {/* Task List */}
      {filteredTasks.length === 0 ? (
        <div className="empty-state">
          <h3>No tasks found</h3>
          <p>
            {tasks.length === 0
              ? "Create your first task to get started"
              : "Try adjusting your filters"}
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredTasks.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="roadmap-task-list">
              {filteredTasks.map((task) => (
                <SortableTask
                  key={task.id}
                  task={task}
                  onEdit={(task) => {
                    setEditingTask(task);
                    setShowDialog(true);
                  }}
                  onDelete={handleDeleteTask}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Task Dialog */}
      {showDialog && (
        <TaskDialog
          task={editingTask}
          onSave={editingTask ? handleEditTask : handleAddTask}
          onClose={() => {
            setShowDialog(false);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
};

export default Roadmap;
